// src/components/routes/RouteForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Form, FormGroup, Label, Input, Row, Col, Button, FormFeedback, Alert,
    Modal, ModalHeader, ModalBody, ModalFooter
} from "reactstrap";
import Session from '../auth/Session';

const TARGET_OPTIONS = [
    { value: "SubCustomer", label: "Sub-Customer" },
    { value: "Route", label: "Route" },
    { value: "Driver", label: "Driver" },
];

const SurchargeForm = ({
    surchargeItem,
    apiBaseUrl,
    onSuccess,
    onCancel,
    allowedTargetTypes = ["SubCustomer", "Route", "Driver"],
    defaultTargetType = "SubCustomer",
}) => {
    const allowedTargetKey = allowedTargetTypes.join("|");
    const targetOptions = TARGET_OPTIONS.filter(option => allowedTargetTypes.includes(option.value));
    const normalizeTargetType = (targetType) => {
        if (allowedTargetTypes.includes(targetType)) return targetType;
        if (allowedTargetTypes.includes(defaultTargetType)) return defaultTargetType;
        return targetOptions[0]?.value ?? "SubCustomer";
    };

    const [formData, setFormData] = useState({
        id: 0,
        subCustomerId: "",
        routeId: "",
        driverId: "",
        targetType: normalizeTargetType(defaultTargetType),
        description: "",
        dollarAmount: 0,
        percentage: 0,
        transactionTypeId: "",
        surchargeTypeId: "",
        driverIds: [],
        isActive: true,
    });

    const [subCustomers, setSubCustomers] = useState([]);
    const [transactionTypes, setTransactionTypes] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [allSurcharges, setAllSurcharges] = useState([]);
    const [showDriverSelector, setShowDriverSelector] = useState(false);
    const [driverSearch, setDriverSearch] = useState("");

    const [errors, setErrors] = useState({});
    const subCustomersApi = "/api/subcustomers";
    const driversApi = "/api/drivers/list";
    const surchargesApi = "/api/surcharges";
    const transactionTypeApi = "/api/TransactionType/GetTransactionTypesAsync";
    const routesBySubCustomerApi = (subCustomerId) => `/api/routes/BySubCustomer/${subCustomerId}`;

    useEffect(() => {
        if (surchargeItem) {
            const targetType = normalizeTargetType(surchargeItem.targetType ?? (surchargeItem.driverId ? "Driver" : (surchargeItem.routeId ? "Route" : defaultTargetType)));
            setFormData({
                id: surchargeItem.id,
                subCustomerId: targetType === "Driver" ? "" : (surchargeItem.subCustomerId ?? ""),
                routeId: targetType === "Route" ? (surchargeItem.routeId ?? "") : "",
                driverId: targetType === "Driver" ? (surchargeItem.driverId ?? "") : "",
                targetType,
                description: surchargeItem.description ?? "",
                surchargeTypeId: surchargeItem.surchargeTypeId ?? "",
                transactionTypeId: surchargeItem.transactionTypeId ?? "",
                dollarAmount: surchargeItem.dollarAmount ?? 0,
                percentage: surchargeItem.percentage ?? 0,
                driverIds: getInitialDriverIds(surchargeItem),
                isActive: surchargeItem.isActive ?? true,
            });
        }
    }, [surchargeItem, allowedTargetKey, defaultTargetType]);

    useEffect(() => {
        const loadData = async () => {
            const token = await Session.getToken();
            // Load dropdown data
            const [res, ttRes, driverRes, surchargeRes] = await Promise.all([
                fetch(subCustomersApi, { headers: { Authorization: `Bearer ${token}` } }),
                fetch(transactionTypeApi, { headers: { Authorization: `Bearer ${token}` } }),
                fetch(driversApi, { headers: { Authorization: `Bearer ${token}` } }),
                fetch(surchargesApi, { headers: { Authorization: `Bearer ${token}` } }),
            ]);

            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            setSubCustomers(data);
            setTransactionTypes(ttRes.ok ? await ttRes.json() : []);
            setDrivers(driverRes.ok ? await driverRes.json() : []);
            setAllSurcharges(surchargeRes.ok ? await surchargeRes.json() : []);
        };
        loadData();
    }, []);

    // Reload the route list whenever the selected sub-customer changes.
    useEffect(() => {
        if (!formData.subCustomerId) {
            setRoutes([]);
            return;
        }
        const loadRoutes = async () => {
            const token = await Session.getToken();
            const response = await fetch(routesBySubCustomerApi(formData.subCustomerId), {
            headers: { Authorization: `Bearer ${token}` }
            });
            setRoutes(response.ok ? await response.json() : []);
        };
        loadRoutes().catch(() => setRoutes([]));
    }, [formData.subCustomerId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(p => {
            const nextValue = e.target.type === "checkbox" ? e.target.checked : value;
            const next = { ...p, [name]: name.endsWith("Rate") || name === "quantity" ? Number(nextValue) : nextValue };
            // Changing sub-customer invalidates any previously selected route.
            if (name === "subCustomerId") next.routeId = "";
            if (name === "targetType") {
                next.subCustomerId = value === "Driver" ? "" : next.subCustomerId;
                next.routeId = "";
                next.driverId = value === "Driver" ? next.driverId : "";
            }
            return next;
        });
    };

    const getDriverIdsForSurcharge = (surcharge) => {
        const ids = surcharge?.driverIds || surcharge?.DriverIds || [];
        const assignmentIds = (surcharge?.driverAssignments || surcharge?.DriverAssignments || [])
            .map(a => a.driverId ?? a.DriverId)
            .filter(Boolean);
        const legacyId = surcharge?.driverId ?? surcharge?.DriverId;
        return [...ids, ...assignmentIds, legacyId]
            .filter(Boolean)
            .map(Number)
            .filter((value, index, all) => all.indexOf(value) === index);
    };

    function getInitialDriverIds(item) {
        return getDriverIdsForSurcharge(item);
    }

    const getDriverLabel = (driver) =>
        `${driver.driverName}${driver.businessName ? ` — ${driver.businessName}` : ""}`;

    const driverConflictById = useMemo(() => {
        const conflicts = new Map();
        if (formData.targetType !== "Driver" || !formData.surchargeTypeId) return conflicts;

        (allSurcharges || []).forEach(surcharge => {
            const targetType = surcharge.targetType ?? (surcharge.driverId ? "Driver" : (surcharge.routeId ? "Route" : "SubCustomer"));
            const isConflictSurcharge = targetType === "Driver"
                && surcharge.isActive !== false
                && `${surcharge.id}` !== `${formData.id}`
                && `${surcharge.surchargeTypeId}` === `${formData.surchargeTypeId}`;

            if (!isConflictSurcharge) return;

            getDriverIdsForSurcharge(surcharge).forEach(driverId => {
                if (!conflicts.has(driverId)) {
                    conflicts.set(driverId, surcharge);
                }
            });
        });

        return conflicts;
    }, [allSurcharges, formData.id, formData.surchargeTypeId, formData.targetType]);

    const getDriverConflict = (driverId) => {
        return driverConflictById.get(Number(driverId)) || null;
    };

    const handleDriverSelection = (driverId, checked) => {
        const numericDriverId = Number(driverId);
        if (checked) {
            const conflict = getDriverConflict(numericDriverId);
            if (conflict && !window.confirm(`${drivers.find(d => d.id === numericDriverId)?.driverName || "This driver"} is already associated with "${conflict.description}" for the selected surcharge type. Add them to this surcharge anyway?`)) {
                return;
            }
        }

        setFormData(prev => {
            const current = prev.driverIds || [];
            const nextDriverIds = checked
                ? [...current, numericDriverId]
                : current.filter(id => Number(id) !== numericDriverId);
            return {
                ...prev,
                driverIds: nextDriverIds.filter((value, index, all) => all.indexOf(value) === index),
                driverId: nextDriverIds[0] || ""
            };
        });
    };

    const filteredDrivers = drivers.filter(driver => {
        const search = driverSearch.trim().toLowerCase();
        if (!search) return true;
        return getDriverLabel(driver).toLowerCase().includes(search);
    });

    const validate = () => {
        const v = {};
        if (!formData.description) {
            v.description = "Surcharge description is required";
        } else {
        }

        if (formData.targetType === "SubCustomer" && !formData.subCustomerId) v.subCustomerId = "Sub-Customer is required";
        if (formData.targetType === "Route" && !formData.routeId) v.routeId = "Route is required";
        if (formData.targetType === "Route" && !formData.subCustomerId) v.subCustomerId = "Sub-Customer is required";
        if (!formData.transactionTypeId) v.transactionTypeId = "Transaction type is required";
        if (formData.surchargeTypeId === "") v.surchargeTypeId = "Surcharge type is required";
        setErrors(v);
        return Object.keys(v).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const method = `${surchargeItem.id}` !== `0` ? "PUT" : "POST";
        const url = `${surchargeItem.id}` !== `0` ? `${apiBaseUrl}/${formData.id}` : apiBaseUrl;

        const body = {
            ...formData,
            subCustomerId: formData.targetType === "Driver" || formData.subCustomerId === "" ? null : Number(formData.subCustomerId),
            routeId: formData.targetType !== "Driver" && formData.routeId !== "" ? Number(formData.routeId) : null,
            driverId: formData.targetType === "Driver" && (formData.driverIds || []).length === 1 ? Number(formData.driverIds[0]) : null,
            driverIds: formData.targetType === "Driver" ? (formData.driverIds || []).map(Number) : [],
            dollarAmount: formData.dollarAmount === "" ? 0 : Number(formData.dollarAmount),
            percentage: formData.percentage === "" ? 0 : Number(formData.percentage),
        };

        const token = await Session.getToken();
        const res = await fetch(url, {
            method,
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(body),
        });

        if (!res.ok) {
            const t = await res.text();
            alert(`Save failed: ${res.status} ${t}`);
            return;
        }
        onSuccess(await res.json());
    };

    return (
        <Form onSubmit={handleSubmit}>
            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label for="targetType">Surcharge Applies To</Label>
                        <Input
                            id="targetType"
                            name="targetType"
                            type="select"
                            value={formData.targetType}
                            onChange={handleChange}
                            disabled={targetOptions.length === 1}
                        >
                            {targetOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
            </Row>

            {formData.targetType === "Driver" ? (
                <Row>
                    <Col md={12}>
                        <FormGroup>
                            <Label>Drivers</Label>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <Button color="secondary" type="button" onClick={() => setShowDriverSelector(true)}>
                                    Select Drivers
                                </Button>
                                <span className="text-muted">{(formData.driverIds || []).length} selected</span>
                            </div>
                            {(formData.driverIds || []).length === 0 && (
                                <Alert color="info" className="mb-0">
                                    No drivers selected. This surcharge will remain available for manual selection.
                                </Alert>
                            )}
                            <FormFeedback>{errors.driverId}</FormFeedback>
                        </FormGroup>
                    </Col>
                </Row>
            ) : (
                <Row>
                    <Col md={6}>
                        <FormGroup>
                        <Label for="subCustomerId">Sub-Customer</Label>
                        <Input
                            id="subCustomerId"
                            name="subCustomerId"
                            type="select"
                            value={formData.subCustomerId}
                            onChange={handleChange}
                            invalid={!!errors.subCustomerId}
                        >
                            <option value="">-- Select Sub-Customer --</option>
                            {subCustomers.map(sc => (
                                <option key={sc.id} value={sc.id}>
                                    {sc.name} {sc.customer ? `— ${sc.customer.name}` : ""}
                                </option>
                            ))}
                        </Input>
                        <FormFeedback>{errors.subCustomerId}</FormFeedback>
                    </FormGroup>
                </Col>

                <Col md={6}>
                    <FormGroup>
                        <Label for="routeId">{formData.targetType === "Route" ? "Route" : "Route (optional)"}</Label>
                        <Input
                            id="routeId"
                            name="routeId"
                            type="select"
                            value={formData.routeId}
                            onChange={handleChange}
                            disabled={!formData.subCustomerId}
                            invalid={!!errors.routeId}
                        >
                            <option value="">{formData.targetType === "Route" ? "-- Select Route --" : "Default (applies to all routes for this sub-customer)"}</option>
                            {routes.map(r => (
                                <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                        </Input>
                        <FormFeedback>{errors.routeId}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>
            )}

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label>Transaction Type</Label>
                        <Input
                            id="transactionTypeId"
                            name="transactionTypeId"
                            type="select"
                            value={formData.transactionTypeId || ""}
                            onChange={handleChange}
                            invalid={!!errors.transactionTypeId}
                        >
                            <option value="">-- Transaction Type --</option>
                            {transactionTypes.map(tt => (
                                <option key={tt.id} value={tt.id}>
                                    {tt.transactionName}
                                </option>
                            ))}
                        </Input>
                        <FormFeedback>{errors.transactionTypeId}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={12}>
                    <FormGroup>
                        <Label>Description</Label>
                        <Input name="description" value={formData.description} onChange={handleChange} invalid={!!errors.description} />
                        <FormFeedback>{errors.description}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label>Surcharge Type</Label>
                        <Input
                            id="surchargeTypeId"
                            name="surchargeTypeId"
                            type="select"
                            value={formData.surchargeTypeId || "Dollar Amount"}
                            onChange={handleChange}
                            invalid={!!errors.surchargeTypeId}
                        >
                            <option value="">-- Surcharge Type --</option>
                            <option value="1">Dollar Amount</option>
                            <option value="7">Dollar Amount Per Day</option>
                            <option value="2">Percentage</option>
                            <option value="3">Fuel mileage</option>
                            <option value="4">Wear and Tear mileage</option>
                            <option value="5">Calculated Fuel mileage</option>
                            <option value="6">Calculated Wear and Tear mileage</option>
                        </Input>
                        <FormFeedback>{errors.surchargeTypeId}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    {`${formData.surchargeTypeId}` === '2' ? (
                        <FormGroup>
                            <Label>Percentage</Label>
                            <Input name="percentage" type="number" step="1" value={formData.percentage} onChange={handleChange} />
                        </FormGroup>
                    ) : (
                        <>
                            {(`${formData.surchargeTypeId}` === '1' || `${formData.surchargeTypeId}` === '7' || `${formData.surchargeTypeId}` === '3' || `${formData.surchargeTypeId}` === '4') && (
                                <FormGroup>
                                    <Label>Dollar Amount</Label>
                                    <Input name="dollarAmount" type="number" step="1" value={formData.dollarAmount} onChange={handleChange} />
                                </FormGroup>
                            )}
                        </>
                    )}
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <FormGroup check>
                        <Input
                            id="isActive"
                            name="isActive"
                            type="checkbox"
                            checked={!!formData.isActive}
                            onChange={handleChange}
                        />
                        <Label for="isActive" check>Active</Label>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <FormGroup>
                    </FormGroup>
                </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2">
                <Button color="secondary" type="button" onClick={onCancel}>Cancel</Button>
                <Button color="primary" type="submit">{`${surchargeItem.id}` !== `0` ? "Update" : "Create"}</Button>
            </div>

            <Modal
                isOpen={showDriverSelector}
                toggle={() => setShowDriverSelector(false)}
                size="lg"
                zIndex={1060}
            >
                <ModalHeader toggle={() => setShowDriverSelector(false)}>
                    Select Drivers
                </ModalHeader>
                <ModalBody>
                    <Input
                        className="mb-3"
                        placeholder="Search drivers..."
                        value={driverSearch}
                        onChange={(e) => setDriverSearch(e.target.value)}
                    />
                    <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                        {filteredDrivers.length === 0 ? (
                            <div className="text-muted text-center py-4">No drivers found</div>
                        ) : filteredDrivers.map(driver => {
                            const selected = (formData.driverIds || []).map(Number).includes(driver.id);
                            const conflict = getDriverConflict(driver.id);
                            return (
                                <FormGroup check key={driver.id} className="mb-2">
                                    <Input
                                        id={`driver-${driver.id}`}
                                        type="checkbox"
                                        checked={selected}
                                        onChange={(e) => handleDriverSelection(driver.id, e.target.checked)}
                                    />
                                    <Label for={`driver-${driver.id}`} check>
                                        {getDriverLabel(driver)}
                                        {conflict && (
                                            <span className="text-warning"> - already on {conflict.description}</span>
                                        )}
                                    </Label>
                                </FormGroup>
                            );
                        })}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <span className="text-muted me-auto">{(formData.driverIds || []).length} selected</span>
                    <Button color="primary" type="button" onClick={() => setShowDriverSelector(false)}>
                        Done
                    </Button>
                </ModalFooter>
            </Modal>
        </Form>
    );
};

export default SurchargeForm;
